/**
 * Shared subscription status utilities.
 *
 * Centralises the logic for determining whether a subscription is truly active,
 * expired, or in a trial-ended state.  The key edge-case: the backend cron that
 * flips `status` from "trialing" → "expired" runs hourly, so there is a window
 * where `status === 'trialing'` but `trial_end` is already in the past.  Every
 * component that cares about "is the subscription valid right now?" must account
 * for this, so we do it in one place.
 */

export interface SubscriptionLike {
  status?: string | null;
  trial_end?: string | null;
  trial_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  [key: string]: any;
}

/**
 * True when status is 'trialing' but trial_end is in the past.
 * The cron hasn't flipped the status to 'expired' yet.
 */
export function isTrialActuallyExpired(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  return (
    sub.status === 'trialing' &&
    !!sub.trial_end &&
    new Date(sub.trial_end) < new Date()
  );
}

/**
 * True when the subscription grants access to the platform right now.
 * Excludes trials whose trial_end has passed.
 */
export function isSubscriptionActive(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  if (sub.status === 'active') return true;
  if (sub.status === 'trialing' && !isTrialActuallyExpired(sub)) return true;
  if (sub.status === 'pending') return true;
  return false;
}

/**
 * True when the subscription is expired / cancelled / trial-ended.
 * Use this to decide whether to show the plan-selection dialog or block actions.
 */
export function isSubscriptionExpiredOrCancelled(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  if (sub.status === 'expired' || sub.status === 'cancelled') return true;
  if (isTrialActuallyExpired(sub)) return true;
  return false;
}
